/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { useTheme } from "@material-ui/core/styles";
import { useSelector } from "react-redux";
import { useLocation, Link } from "react-router-dom";

import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import grey from "@material-ui/core/colors/grey";

import SearchField from "./SearchField";
import SpotSearchField from "./SpotSearchField";
import BuildAdHocButton from "./adhoc/BuildAdHocButton";
import * as authUtils from "../helpers/auth/authUtils";
import {
  isShipper,
  isShipperManager,
  isAdministrator,
} from "../helpers/goTrackUserUtils";
import InviteNewUserButton from "./user-management/InviteNewUserButton";

const StyledAppHeader = styled(AppBar)`
  background: ${(props) => props.theme.palette.primary.dark} !important;
  height: 60px;
`;

const StyledTypography = styled(Typography)`
  flex-grow: 1;
  display: block;
  font-weight: 500 !important;
  font-size: 1.8rem !important;
`;

const StyledMenu = styled(Menu)`
  .MuiMenu-paper {
    top: 50px !important;
    min-width: 170px;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: #fff;
`;

const AppHeader = (props) => {
  const { pageTitle } = props;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const theme = useTheme();
  const { goTrackUser } = useSelector((state) => state.goTrackUser);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogOutClick = () => {
    authUtils.logOut();
  };

  const location = useLocation();
  const canSeeAdHoc =
    goTrackUser &&
    (isShipper(goTrackUser.roles) || isShipperManager(goTrackUser.roles));

  const canSeeCarrierManagement =
    goTrackUser &&
    (isAdministrator(goTrackUser.roles) || isShipperManager(goTrackUser.roles));

  const canSeeUserManagement =
    goTrackUser &&
    (isAdministrator(goTrackUser.roles) || isShipperManager(goTrackUser.roles));

  const canInviteNewUser =
    goTrackUser &&
    (isAdministrator(goTrackUser.roles) || isShipperManager(goTrackUser.roles));

  return (
    <StyledAppHeader theme={theme} position="static">
      <Toolbar>
        <StyledTypography
          noWrap
          theme={theme}
          variant="h1"
          data-testid="app-title"
          align="left"
        >
          <StyledLink to="/">{pageTitle && pageTitle}</StyledLink>
        </StyledTypography>
        {location.pathname.startsWith("/tracking") && <SearchField />}
        {location.pathname.startsWith("/availableroutes") && (
          <>
            <SpotSearchField />
            {canSeeAdHoc && <BuildAdHocButton />}
          </>
        )}
        {location.pathname.startsWith("/user-management") &&
          canInviteNewUser && <InviteNewUserButton />}
        {props.contentPlaceholder}
        <div>
          <IconButton
            onClick={handleClick}
            style={{ marginLeft: "20px" }}
            edge="end"
            aria-controls="simple-menu"
            aria-haspopup="true"
            data-testid="user-icon-button"
          >
            <AccountCircleIcon fontSize="large" style={{ color: grey[300] }} />
          </IconButton>
          <StyledMenu
            id="simple-menu"
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            getContentAnchorEl={null}
          >
            {canSeeCarrierManagement && (
              <MenuItem
                data-testid="carrier-management"
                to="/carrier-management"
                component={Link}
              >
                Carrier Management
              </MenuItem>
            )}
            {canSeeUserManagement && (
              <MenuItem
                data-testid="user-management"
                to="/user-management"
                component={Link}
              >
                User Management
              </MenuItem>
            )}
            <MenuItem data-testid="logout-button" onClick={handleLogOutClick}>
              Log out
            </MenuItem>
          </StyledMenu>
        </div>
      </Toolbar>
    </StyledAppHeader>
  );
};

AppHeader.propTypes = {
  pageTitle: PropTypes.string,
  theme: PropTypes.object,
  contentPlaceholder: PropTypes.func,
};

export default AppHeader;
